import * as React from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  required,
  minValue,
  maxValue,
  FormDataConsumer,
  SelectInput,
  BooleanInput,
  ArrayInput,
  SimpleFormIterator,
  SelectArrayInput,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { useClub } from "../club/ClubContext";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Chip,
  Paper,
} from "@mui/material";

/* ---------------- helpers ---------------- */

function parseCsv(input?: string): string[] {
  return (input ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseIntCsv(input?: string): number[] {
  return parseCsv(input)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n));
}

function makeColumnLabels(maxRounds: number) {
  const n = Math.max(1, Math.trunc(maxRounds || 1));
  return Array.from({ length: n }, (_, i) => `R${i + 1}`);
}

function clampInt(n: any, min: number, max: number, fallback: number) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, Math.trunc(v)));
}

function sanitizeAllowed(arr: any): number[] {
  const raw = Array.isArray(arr) ? arr : [3, 2, 1];
  const cleaned = raw
    .map((x) => clampInt(x, 1, 3, 3))
    .filter((v, i, a) => a.indexOf(v) === i);
  return cleaned.length ? cleaned : [3, 2, 1];
}

type X01Rule = any;

function sanitizeX01Rules(data: any) {
  const rules: X01Rule[] = Array.isArray(data?.x01_rules?.darts_rules) ? data.x01_rules.darts_rules : [];
  const cleaned = rules.map((r) => {
    const mode = String(r?.mode ?? "CHOICE").toUpperCase();
    const from = clampInt(r?.from, 0, 999999, 501);
    const to = clampInt(r?.to, 0, 999999, 0);

    if (mode === "FIXED") {
      return {
        from,
        to,
        mode: "FIXED",
        show: false,
        fixed: clampInt(r?.fixed, 1, 3, 3),
      };
    }

    if (mode === "DEFAULT") {
      return {
        from,
        to,
        mode: "DEFAULT",
        show: Boolean(r?.show ?? true),
        allowed: sanitizeAllowed(r?.allowed),
        default: clampInt(r?.default, 1, 3, 3),
      };
    }

    // CHOICE
    return {
      from,
      to,
      mode: "CHOICE",
      show: Boolean(r?.show ?? true),
      allowed: sanitizeAllowed(r?.allowed),
    };
  });

  const qa = data?.x01_rules?.quick_actions ?? {};
  const quick_actions = {
    bust_button: Boolean(qa?.bust_button ?? true),
    from_remaining: clampInt(qa?.from_remaining, 0, 999999, 60),
  };

  return { cleanedRules: cleaned, quick_actions };
}

/* ---------------- Vorschau ---------------- */

function TemplatePreviewButton() {
  const [open, setOpen] = React.useState(false);

  const mode = (useWatch({ name: "input_mode" }) as "choices" | "numeric" | undefined) ?? "choices";
  const x01Enabled = Boolean(useWatch({ name: "x01_enabled" }));

  const rowLabelsCsv = useWatch({ name: "rowLabelsCsv" }) as string | undefined;
  const columnLabelsCsv = useWatch({ name: "columnLabelsCsv" }) as string | undefined;

  const x01MaxRounds = (useWatch({ name: "x01_max_rounds" }) as number | undefined) ?? 40;
  const x01Start = (useWatch({ name: "x01_start_value" }) as number | undefined) ?? 501;

  const numericMin = (useWatch({ name: "numeric_min" }) as number | undefined) ?? 0;
  const numericMax = (useWatch({ name: "numeric_max" }) as number | undefined) ?? 180;
  const dartsPerEntry = (useWatch({ name: "darts_per_entry" }) as number | undefined) ?? 3;

  const rowLabels = React.useMemo(() => {
    if (x01Enabled && mode === "numeric") return ["Runde"];
    return parseCsv(rowLabelsCsv);
  }, [x01Enabled, mode, rowLabelsCsv]);

  const colLabels = React.useMemo(() => {
    if (x01Enabled && mode === "numeric") return makeColumnLabels(x01MaxRounds);
    return parseCsv(columnLabelsCsv);
  }, [x01Enabled, mode, columnLabelsCsv, x01MaxRounds]);

  const previewOk = rowLabels.length > 0 && colLabels.length > 0;

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="outlined" sx={{ borderRadius: 3 }} onClick={() => setOpen(true)} disabled={!previewOk}>
          Vorschau
        </Button>

        <Chip size="small" variant="outlined" label={previewOk ? `${rowLabels.length}×${colLabels.length}` : "—"} />

        {x01Enabled && mode === "numeric" && (
          <Chip size="small" variant="outlined" label={`X01: ${x01Start} / ${x01MaxRounds} Runden`} />
        )}
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vorschau</DialogTitle>
        <DialogContent dividers>
          {!previewOk ? (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Bitte Reihen/Spalten konfigurieren.
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1}>
                <Chip size="small" label={`Mode: ${mode}${x01Enabled ? " (X01)" : ""}`} />
                {mode === "numeric" && (
                  <Chip
                    size="small"
                    label={`Numeric Range: ${numericMin}–${numericMax} / darts_per_entry=${dartsPerEntry}`}
                  />
                )}
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  X01 nutzt automatisch: rows=1, columns=max rounds.
                </Typography>
              </Stack>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained" sx={{ borderRadius: 3 }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ---------------- Create ---------------- */

export function TrainingTemplateCreate() {
  const { clubId } = useClub();

  return (
    <Create
      transform={(data: any) => {
        const mode = (data.input_mode ?? "choices") as "choices" | "numeric";
        const x01Enabled = Boolean(data.x01_enabled) && mode === "numeric";

        const cps = parseIntCsv(data.quoteCheckpointsCsv)
          .map((n) => Math.trunc(n))
          .filter((n) => n > 0);

        const allowedScores = parseIntCsv(data.allowedScoresCsv)
          .map((n) => Math.trunc(n))
          .filter((n) => n >= 0);

        let dartsPerEntry = Math.trunc(Number(data.darts_per_entry ?? 3));
        const numericMin = Math.trunc(Number(data.numeric_min ?? 0));
        const numericMax = Math.trunc(Number(data.numeric_max ?? 180));

        let rowLabels: string[] = [];
        let colLabels: string[] = [];

        if (x01Enabled) {
          const maxRounds = Math.max(1, Math.trunc(Number(data.x01_max_rounds ?? 40)));
          rowLabels = ["Runde"];
          colLabels = makeColumnLabels(maxRounds);

          // ✅ X01: darts_per_entry auf max 3
          dartsPerEntry = clampInt(dartsPerEntry, 1, 3, 3);
        } else {
          rowLabels = parseCsv(data.rowLabelsCsv);
          colLabels = parseCsv(data.columnLabelsCsv);
          if (!rowLabels.length || !colLabels.length) {
            throw new Error("Reihen- und Spalten-Labels sind Pflicht.");
          }
        }

        const {
          rowLabelsCsv,
          columnLabelsCsv,
          quoteCheckpointsCsv,
          allowedScoresCsv,
          x01_enabled,
          x01_start_value,
          x01_max_rounds,
          ...rest
        } = data;

        const out: any = {
          ...rest,
          club_id: clubId,

          rows: rowLabels.length,
          columns: colLabels.length,

          row_labels: rowLabels,
          column_labels: colLabels,

          quote_checkpoints: cps,
          input_mode: mode,

          darts_per_entry:
            mode === "numeric"
              ? Number.isFinite(dartsPerEntry) && dartsPerEntry > 0
                ? dartsPerEntry
                : 3
              : null,

          allowed_scores:
            mode === "choices"
              ? allowedScores.length
                ? allowedScores
                : [0, 1, 2, 3]
              : null,

          numeric_min: mode === "numeric" ? numericMin : null,
          numeric_max: mode === "numeric" ? numericMax : null,
        };

        if (x01Enabled) {
          const { cleanedRules, quick_actions } = sanitizeX01Rules(data);

          out.x01_rules = {
            start_value: Math.max(1, Math.trunc(Number(x01_start_value ?? 501))),
            bust_on_one: true,
            bust_on_negative: true,
            darts_rules: cleanedRules,
            quick_actions,
          };

          // X01 Default: numeric 0..180
          out.numeric_min = 0;
          out.numeric_max = 180;
        } else {
          out.x01_rules = null;
        }

        return out;
      }}
    >
      <SimpleForm defaultValues={{ club_id: clubId, input_mode: "choices", darts_per_entry: 3, x01_enabled: false }}>
        <TextInput source="name" label="Spielname" validate={[required()]} fullWidth />
        <TextInput source="description" label="Beschreibung" fullWidth multiline />

        <SelectInput
          source="input_mode"
          label="Eingabe-Modus"
          choices={[
            { id: "choices", name: "Buttons (choices)" },
            { id: "numeric", name: "Wert-Eingabe (numeric)" },
          ]}
          defaultValue="choices"
          fullWidth
        />

        <FormDataConsumer>
          {({ formData }) => {
            const mode = (formData.input_mode ?? "choices") as "choices" | "numeric";
            return mode === "numeric" ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <BooleanInput source="x01_enabled" label="X01 Countdown aktivieren (501/301…)" />
              </Stack>
            ) : null;
          }}
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) => {
            const mode = (formData.input_mode ?? "choices") as "choices" | "numeric";
            const x01Enabled = Boolean(formData.x01_enabled) && mode === "numeric";

            if (x01Enabled) {
              return (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <NumberInput
                    source="x01_start_value"
                    label="Startwert (z.B. 501)"
                    defaultValue={501}
                    validate={[required(), minValue(1)]}
                  />
                  <NumberInput
                    source="x01_max_rounds"
                    label="Max Runden (columns)"
                    defaultValue={40}
                    validate={[required(), minValue(1), maxValue(200)]}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    label="Hinweis: rows=1 wird automatisch gesetzt, Spaltenlabels werden generiert (R1..Rn)"
                  />

                  <Stack direction="row" spacing={2}>
                    <NumberInput source="numeric_min" label="Numeric Min" defaultValue={0} disabled />
                    <NumberInput source="numeric_max" label="Numeric Max" defaultValue={180} disabled />
                  </Stack>

                  {/* ✅ X01: max 3 */}
                  <NumberInput
                    source="darts_per_entry"
                    label="darts_per_entry (Info/Averages)"
                    defaultValue={3}
                    validate={[minValue(1), maxValue(3)]}
                  />

                  <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                    Darts-Regeln (Remaining Ranges)
                  </Typography>

                  <ArrayInput source="x01_rules.darts_rules" label="">
                    <SimpleFormIterator inline>
                      <NumberInput source="from" label="from (inkl.)" validate={[required()]} />
                      <NumberInput source="to" label="to (inkl.)" validate={[required()]} />

                      <SelectInput
                        source="mode"
                        label="Mode"
                        choices={[
                          { id: "CHOICE", name: "CHOICE (Buttons)" },
                          { id: "FIXED", name: "FIXED (auto)" },
                          { id: "DEFAULT", name: "DEFAULT (preselect + change)" },
                        ]}
                        defaultValue="CHOICE"
                      />

                      {/* ✅ pro Rule: sinnvolle Felder ein/ausblenden */}
                      <FormDataConsumer>
  {({ scopedFormData }) => {
    const rm = String(scopedFormData?.mode ?? "CHOICE").toUpperCase();

    if (rm === "FIXED") {
      return (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <NumberInput
            source="fixed"
            label="fixed (1–3)"
            defaultValue={3}
            validate={[required(), minValue(1), maxValue(3)]}
          />
        </Stack>
      );
    }

    if (rm === "DEFAULT") {
      return (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <BooleanInput source="show" label="Buttons zeigen" defaultValue={true} />

          <SelectArrayInput
            source="allowed"
            label="allowed (1–3)"
            choices={[
              { id: 1, name: "1" },
              { id: 2, name: "2" },
              { id: 3, name: "3" },
            ]}
          />

          <NumberInput
            source="default"
            label="default (1–3)"
            defaultValue={3}
            validate={[required(), minValue(1), maxValue(3)]}
          />
        </Stack>
      );
    }

    // CHOICE
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <BooleanInput source="show" label="Buttons zeigen" defaultValue={true} />

        <SelectArrayInput
          source="allowed"
          label="allowed (1–3)"
          choices={[
            { id: 1, name: "1" },
            { id: 2, name: "2" },
            { id: 3, name: "3" },
          ]}
        />
      </Stack>
    );
  }}
</FormDataConsumer>

                    </SimpleFormIterator>
                  </ArrayInput>

                  <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                    Quick Actions
                  </Typography>

                  <Stack direction="row" spacing={2}>
                    <NumberInput
                      source="x01_rules.quick_actions.from_remaining"
                      label="Bust-Button ab Remaining ≤"
                      defaultValue={60}
                      validate={[minValue(0), maxValue(9999)]}
                    />
                    <BooleanInput
                      source="x01_rules.quick_actions.bust_button"
                      label="Bust Button aktiv"
                      defaultValue={true}
                    />
                  </Stack>

                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    DEFAULT = vorselektiert, aber änderbar (allowed). FIXED = nicht änderbar.
                  </Typography>
                </Stack>
              );
            }

            // Nicht-X01 -> alter CSV Weg
            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextInput
                  source="rowLabelsCsv"
                  label="Reihen (Komma-Liste)"
                  fullWidth
                  validate={[required()]}
                  helperText='z.B. "R1,R2,R3"'
                />
                <TextInput
                  source="columnLabelsCsv"
                  label="Spalten (Komma-Liste)"
                  fullWidth
                  validate={[required()]}
                  helperText='z.B. "C1,C2,C3,C4"'
                />

                {mode === "choices" ? (
                  <TextInput
                    source="allowedScoresCsv"
                    label="Erlaubte Werte (Komma-Liste)"
                    fullWidth
                    helperText='z.B. "0,1,2,3"'
                  />
                ) : (
                  <Stack direction="row" spacing={2}>
                    <NumberInput source="numeric_min" label="Numeric Min" defaultValue={0} />
                    <NumberInput source="numeric_max" label="Numeric Max" defaultValue={180} />
                    <NumberInput source="darts_per_entry" label="darts_per_entry" defaultValue={3} validate={[minValue(1), maxValue(9)]} />
                  </Stack>
                )}

                <TextInput
                  source="quoteCheckpointsCsv"
                  label="Checkpoints (Komma-Liste)"
                  fullWidth
                  helperText='z.B. "3,6,10"'
                />
              </Stack>
            );
          }}
        </FormDataConsumer>

        <TemplatePreviewButton />
      </SimpleForm>
    </Create>
  );
}
